/*
 *
 *
 *
 * README:
 *
 * Our `yi-graphql` package currently does not support @yi/core imports.
 *
 * Until we can support those imports, any changes committed to modules within the
 * `yi-core/` folder should include changes to the corresponding module in @yi/core.
 *
 * This module's corresponding location is: `yi-graphql/src/yi-core/config.js`
 *
 *
 *
 */

/**
 * DO NOT PUT PRIVATE KEYS IN THIS MODULE
 *
 * This module contains environment variables that are safe to expose to the browser
 *
 * Private keys and other secrets must be kept in a .env file, outside version control
 */

const prodPlans = {
  'YI 1W MO BASIC': '8a12911280d6d5050180dd6dcdd36210',
  'YI 1W YR BASIC': '8a128e2d80d6b92c0180dd6b593526f3',
  'YI MO BASIC': '8a128d7080d6b93c0180dd64651a22ff',
  'YI YR BASIC': '8a12906880d6d5260180dd676a234560',
  'YI YR PREMIUM': '8a128d7080d6b93c0180dd68f6f2566b',
  'YI MO PREMIUM': '8a1295918180b1aa0181829fa92c71b0',
  'GTV CM 00 1M': '2c92a00768760ab9016882b3de04203c',
  'YI YR BASIC WB1': '8a12989f8962fb6a018965d902911e9e',
  // 'YI YR BASIC WB2': '',
  'YI 3MO MO BASIC': '8a129c288ff2ec13018ff333b4333ddd',
  'YI 3MO MO CD': '8a128912912ca8f501912dc469365743',
};

const stagingPlans: typeof prodPlans = {
  'YI 1W MO BASIC': '8ad0824e7ebda078017ec0b49ae350ff',
  'YI 1W YR BASIC': '8ad08c0f7ebc0567017ec0b7c3862eef',
  'YI MO BASIC': '8ad08c0f7ebc0567017ec0ac498e0269',
  'YI YR BASIC': '8ad09f8a7ebafe46017ec0b0517f794c',
  'YI YR PREMIUM': '8ad09f8a7ebafe46017ec0bddab77e44',
  'YI MO PREMIUM': '8ad09fc2816cd265018182a580263f1c',
  'GTV CM 00 1M': '2c92c0f867cae0610167f0be07ed01dd',
  'YI YR BASIC WB1': '8ad094b9893e4d81018941dc8ce954e6',
  // 'YI YR BASIC WB2': '8ad096ca893e4d8a018941e008aa636b',
  'YI 3MO MO BASIC': '8ad081dd8ff24a9a018ff34360213806',
  'YI 3MO MO CD': '8ad097b4912c07fe01912dbd89333894',
};

export type Environment = 'development' | 'experimental' | 'staging' | 'production' | 'test';

const config = {
  // Zuora's `productRatePlanId` by `planSku`
  plans: { staging: stagingPlans, production: prodPlans },

  BROOKLYN_URL: {
    staging: 'https://brooklyn-stage-f7ydas.gaia.com',
    production: 'https://brooklyn.gaia.com',
  },
  FINGERPRINTJS_API_KEY: {
    staging: 'qYt2XUGzX0WFLR6zO9AC',
    production: 'P2wt6c7lsHjU9Rc3WbqK',
  },
  FINGERPRINTJS_LOADER_FALLBACK: {
    staging: 'https://yi-scripts.s3.amazonaws.com/fpjs_loader_v3.11.7_staging.js',
    production: 'https://yi-scripts.s3.amazonaws.com/fpjs_loader_v3.11.7_production.js',
  },
  FINGERPRINTJS_ENDPOINT: {
    staging: 'https://stgmetrics.gaia.com',
    production: 'https://metrics.gaia.com',
  },
  ZUORA_IFRAME_CREDIT_CARD_PAGE_ID: {
    development: '8ad098317ee1bf0e017eea18f5f734a8',
    experimental: '8ad098317ee1bf0e017eea1ba1f255cf',
    staging: '8ad099158258af4501825ae514827da2',
    production: '8a1294c1828218090182894a6e6656b0',
  },
  ZUORA_IFRAME_URL: {
    staging: 'https://apisandbox.zuora.com/apps/PublicHostedPageLite.do',
    production: 'https://www.zuora.com/apps/PublicHostedPageLite.do',
  },
  ADYEN_CLIENT_KEY: {
    staging: 'test_WEB7BMDQ2REGJOMEWNWFCEFXA4LNXH24',
    production: 'live_IAMQ3KQ53JGVLO6TLYTBJGVLHUOHBG6W',
  },
  ADYEN_ENVIRONMENT: {
    staging: 'test',
    production: 'live',
  },
  RECAPTCHA_SITE_KEY: {
    development: '6LcgPuElAAAAAN1SkzTl8mJjzwkxer5nJ5yIIyt9',
    experimental: '6LcgPuElAAAAAN1SkzTl8mJjzwkxer5nJ5yIIyt9',
    staging: '6LcgPuElAAAAAN1SkzTl8mJjzwkxer5nJ5yIIyt9',
    production: '6Le1K-ElAAAAABFyJyO7xaAwZxNcsXYTQsR5F8RS',
  },
  ZENDESK_KEY: {
    staging: '3bde84a9-0dc2-4625-8381-208424512068', // Zendesk sandbox
    production: '451fb1de-f8c1-4019-90bc-758cf44c54a8',
  },
};

// Ensure that `config` values match expected shape without losing type safety on the keys
type ValidateType<
  C extends { [k: string]: undefined | { staging: unknown; production: unknown } },
> = C;
export type ValidatedType = ValidateType<typeof config>;

export const getConfig = (env: Environment, key: keyof typeof config) => {
  const fallback = 'staging';

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const value = config[key]?.[env];

  return value || config[key]?.[fallback];
};
